import { useState } from 'react'
import { format } from 'date-fns'
import { PiCalendarBlank, PiCaretDown, PiX } from 'react-icons/pi'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Calendar } from 'components/UI/calendar'

export type DateRangeValue = { from: Date; to?: Date } | undefined

/** Reusable date range picker with Popover + Calendar.
 *  Selections auto-apply via `onChange` so clicking outside the popover
 *  preserves the picked range. The explicit "Apply" footer button just
 *  closes the popover (data is already applied). */
export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRangeValue
  onChange: (range: DateRangeValue) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex-1 flex items-center gap-2 px-3.5 py-2 bg-white border border-line rounded-md text-base font-medium text-fg-secondary hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] transition-shadow"
        >
          <PiCalendarBlank className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-left">
            {value?.from ? (
              value.to ? (
                <>
                  {format(value.from, 'd MMM yyyy')} -{' '}
                  {format(value.to, 'd MMM yyyy')}
                </>
              ) : (
                format(value.from, 'd MMM yyyy')
              )
            ) : (
              'All time'
            )}
          </span>
          {value?.from ? (
            // Inline clear affordance — uses span (not button) to
            // avoid nesting a button inside the popover trigger.
            // stopPropagation keeps the popover closed on clear.
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date range"
              onClick={(e) => {
                e.stopPropagation()
                onChange(undefined)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(undefined)
                }
              }}
              className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-fg-secondary hover:bg-gray-100 hover:text-fg transition-colors"
            >
              <PiX className="w-4 h-4" />
            </span>
          ) : (
            <PiCaretDown className="w-5 h-5 flex-shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={value?.from}
          selected={value}
          onSelect={(newRange) => {
            if (!newRange?.from) return
            onChange(newRange as { from: Date; to?: Date })
          }}
          numberOfMonths={2}
          classNames={{
            day: 'inline-flex items-center justify-center text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 h-9 w-9 p-0 text-fg aria-selected:opacity-100 hover:bg-surface-hover rounded-full',
            day_selected: 'rounded-full',
            day_today: 'font-semibold text-fg',
            day_range_start:
              'day-range-start bg-brand-active text-white rounded-full hover:bg-brand-active hover:text-white focus:bg-brand-active focus:text-white',
            day_range_end:
              'day-range-end bg-brand-active text-white rounded-full hover:bg-brand-active hover:text-white focus:bg-brand-active focus:text-white',
            day_range_middle:
              'day-range-middle aria-selected:bg-transparent aria-selected:text-fg rounded-none hover:bg-transparent',
            cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20 [&:has(.day-range-middle)]:bg-gray-100 [&:has(.day-range-start)]:bg-gradient-to-r [&:has(.day-range-start)]:from-transparent [&:has(.day-range-start)]:from-50% [&:has(.day-range-start)]:to-gray-100 [&:has(.day-range-start)]:to-50% [&:has(.day-range-end)]:bg-gradient-to-l [&:has(.day-range-end)]:from-transparent [&:has(.day-range-end)]:from-50% [&:has(.day-range-end)]:to-gray-100 [&:has(.day-range-end)]:to-50%',
            head_cell: 'text-fg-secondary w-9 font-normal text-xs pb-2',
          }}
        />
        <div className="flex items-center justify-between gap-2 p-3 border-t">
          {/* Clear sits on the left, Apply on the right. Clear is
              disabled when there's nothing to clear so the footer
              layout stays consistent (Apply remains anchored right). */}
          <button
            type="button"
            onClick={() => onChange(undefined)}
            disabled={!value}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Clear dates
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center px-4 py-1.5 rounded-md bg-brand-hover text-white text-sm font-medium hover:bg-brand-dark transition-colors"
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
